import classNames from "classnames";
import Head from "next/head";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import IconDoneReviews from "@/icons/icon-done";
import Button from "~/components/common/button";
import Icon from "~/components/common/icon";
import { useConfiguration } from "~/contexts/configuration";
import { KikReviewsV2Response } from "~/shared/power-reviews-response.interface";
import prUtils from "~/utils/power-reviews-utils";

import Container from "../../common/container";
import styles from "./product-reviews.module.scss";
import ReviewImagesCarousel from "./review-images-carousel";
import ReviewItem from "./review-item";
import ReviewStars from "./review-stars";
import ProductReviewsBestFor from "./reviews-bestfor";
import ReviewsHistogram from "./reviews-histogram";

type PropsProductReviews = {
  reviews: KikReviewsV2Response;
};

export default function ProductReviews(props: PropsProductReviews) {
  const t = useTranslations();
  const { reviews } = props;
  const { organizationUnitId } = useConfiguration();
  const router = useRouter();
  const reviewsResult = reviews?.results[0];
  const rollup = reviewsResult?.rollup;
  const reviewsSectionRef = useRef<HTMLElement>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalResults, setTotalResults] = useState<number>(reviews?.paging.total_results);
  const [reviewItems, setReviewItems] = useState(reviewsResult?.reviews || []);
  const [reviewsFilters, setReviewsFilters] = useState<{ rating?: number; bestfor1?: string[] }>({});
  const averageRatingFixed = Number(rollup?.average_rating.toFixed(1));
  const bestFor = rollup?.properties.find((p) => p.key === "bestfor1");
  const bestForValues = bestFor?.values;

  const filterObject = useMemo(() => {
    let filters: string[] = [];
    Object.entries(reviewsFilters).forEach(([key, value]) => {
      if (key === "rating") {
        filters.push(`rating:${value}`);
      } else if (key === "bestfor1") {
        const bestForTags = (value as string[]).join("||");
        filters.push(`bestfor1:${bestForTags}`);
      }
    });
    return filters.length > 0 && { filters: filters.join(",") };
  }, [reviewsFilters]);

  const getReviews = useCallback(
    async (params: Object) => {
      const reviewsResponse = await prUtils.getReviews(
        reviewsResult.page_id,
        organizationUnitId,
        router.locale!,
        params
      );
      if (reviewsResponse) {
        setTotalResults(reviewsResponse.paging.total_results);
        return reviewsResponse.results[0].reviews;
      } else {
        return [];
      }
    },
    [reviewsResult.page_id, organizationUnitId, router.locale]
  );

  useEffect(() => {
    setCurrentPage(1);
    getReviews({ ...filterObject }).then((reviewItemsResponse) => {
      setReviewItems(reviewItemsResponse);
    });
  }, [filterObject, getReviews]);

  if (!reviewsResult || !rollup) {
    return null;
  }

  const loadMore = async () => {
    const reviewItemsResponse = await getReviews({
      pageFrom: currentPage * reviews!.paging.page_size,
      ...filterObject,
    });
    setCurrentPage(currentPage + 1);
    setReviewItems([...reviewItems, ...reviewItemsResponse]);
  };

  const reviewsFilterByStars = async (rating: number) => {
    if (reviewsFilters.rating === rating) {
      setReviewsFilters(({ rating, ...rest }) => rest);
    } else {
      setReviewsFilters({ ...reviewsFilters, rating: rating });
    }
  };

  const reviewsFilterByTag = async (tag: string) => {
    let tagFilters = reviewsFilters.bestfor1 ? [...reviewsFilters.bestfor1] : [];
    if (reviewsFilters.bestfor1?.includes(tag)) {
      tagFilters = tagFilters.filter((t) => t !== tag);
    } else {
      tagFilters.push(tag);
    }

    if (tagFilters.length > 0) {
      setReviewsFilters({ ...reviewsFilters, bestfor1: tagFilters });
    } else {
      setReviewsFilters(({ bestfor1, ...rest }) => rest);
    }
  };

  const recommended_Ratio = 80;
  // const bestForValues = [
  //   {
  //     label: "Oficina",
  //     count: 61,
  //   },
  //   {
  //     label: "Salir de parranda",
  //     count: 44,
  //   },
  //   {
  //     label: "Viaje",
  //     count: 16,
  //   },
  // ];

  return (
    <Container className={classNames(styles.container, styles.powerReviewsOuterContainer)} id="product-reviews">
      <p className={styles.title}>{t("generic.reviews")}</p>
      <div className={styles.powerReviewsContainer}>
        <ReviewImagesCarousel media={rollup?.media} />
        <section className={styles.reviewsSnapshot}>
          <div className={styles.reviewsSnapshotHeader}>
            <div className={styles.reviewsSnapshotHeaderSnippet}>
              {recommended_Ratio && (
                <div className={styles.reviewsRecoRatio}>
                  <div className={styles.reviewsRecoApproval}>
                    {recommended_Ratio >= 75 && (
                      <Icon icon={IconDoneReviews} height={45} width={45} className={styles.reviewsIconDone} />
                    )}
                    <span className={styles.reviewsRecoValue}>{`${recommended_Ratio}%`}</span>
                  </div>
                  <span className={styles.reviewsRecoText}>{t("reviews.recommendations")}</span>
                </div>
              )}
              <div className={styles.reviewsStarsContainer}>
                <div className={styles.reviewsStarsDecimal} aria-hidden="true">
                  {averageRatingFixed.toFixed(1)}
                </div>
                <ReviewStars rating={averageRatingFixed} id={reviewsResult.page_id} inQuarters />
              </div>
              <div className={styles.reviewsCount}>{`${rollup.review_count} ${t("generic.reviews")}`}</div>
            </div>
          </div>
          <ReviewsHistogram
            rollup={rollup}
            reviewsFilterByStars={reviewsFilterByStars}
            starsFilter={reviewsFilters.rating}
          />
          {bestForValues && bestForValues.length > 0 && (
            <ProductReviewsBestFor
              bestForValues={bestForValues}
              filterByTag={reviewsFilterByTag}
              tagFilters={reviewsFilters.bestfor1}
            />
          )}
        </section>
        <section className={styles.reviewsItems} ref={reviewsSectionRef}>
          {reviewItems.map((rev) => (
            <ReviewItem review={rev} key={rev.review_id} />
          ))}
          {totalResults > reviewItems.length && (
            <div className={styles.reviewsButtonContainer}>
              <Button variant="secondary" className={styles.reviewsButtonLoadmore} onClick={loadMore}>
                {t("reviews.loadMore")}
              </Button>
            </div>
          )}
        </section>
      </div>
    </Container>
  );
}
